'use client';

import {
  easeInOut,
  motion,
  useInView,
  useTransform,
  type MotionValue,
} from 'motion/react';
import { useTranslations } from 'next-intl';
import { useRef, type ReactNode } from 'react';

import BackgroundVideo from '@/components/background-video';

import ScrollStage from './_components/scroll-stage';
import { useStoryReached } from './_components/useStoryReached.hook';
import { customRichTextTags } from './main-responsive';

import transformPhonePoster from '@images/main/main-transform-portrait-poster.webp';
import transformPoster from '@images/main/main-transform-poster.webp';

const LIGHT_BACKGROUND =
  'radial-gradient(1327px 747px at 50% 50%, #D4E3F1 0%, #FFFFFF 100%)';

const headline =
  'px-5 py-12 text-center text-[24px] font-semibold leading-8 tracking-[-0.464px] xl:tracking-[-1.072px] xl:text-[48px] xl:leading-[56px]';

function DarkStage({ answer, active }: { answer: ReactNode; active: boolean }) {
  return (
    <div className="relative grid w-full [min-height:inherit]">
      <div className="absolute inset-0 overflow-hidden bg-[#0D0E10] group-data-[sticky=true]/scroll-stage:top-[calc(-1*var(--scroll-stage-top))]">
        <BackgroundVideo
          active={active}
          poster={transformPoster}
          phonePoster={transformPhonePoster}
          name="main-transform"
          wideRes="720"
          narrowRes="720"
          phoneRes="portrait"
        />
      </div>
      <p
        className={`${headline} relative flex items-center justify-center text-white`}
      >
        {answer}
      </p>
    </div>
  );
}

function TransitionStage({
  progress,
  question,
  answer,
  active,
}: {
  progress: MotionValue<number>;
  question: ReactNode;
  answer: ReactNode;
  active: boolean;
}) {
  const questionOpacity = useTransform(progress, [0.15, 0.55], [1, 0], {
    ease: easeInOut,
  });
  const darkOpacity = useTransform(progress, [0.15, 0.55], [0, 1], {
    ease: easeInOut,
  });

  return (
    <div className="relative isolate grid w-full [min-height:inherit]">
      <div
        aria-hidden="true"
        className="absolute inset-0 group-data-[sticky=true]/scroll-stage:top-[calc(-1*var(--scroll-stage-top))]"
        style={{ background: LIGHT_BACKGROUND }}
      />
      <motion.p
        style={{ opacity: questionOpacity }}
        className={`${headline} relative col-start-1 row-start-1 flex items-center justify-center text-3b-text-primary`}
      >
        {question}
      </motion.p>
      <motion.div
        style={{ opacity: darkOpacity }}
        className="col-start-1 row-start-1 grid [min-height:inherit]"
      >
        <DarkStage answer={answer} active={active} />
      </motion.div>
    </div>
  );
}

export default function TransitionSection() {
  const t = useTranslations('Main.transition');
  const ref = useRef<HTMLElement>(null);
  // 화면에 닿고서야 받으면 도착했을 때 아직 비어 있다. 스토리에 들어선 시점부터
  // 받아 둔다 — 히어로만 보고 떠나는 사람은 여전히 받지 않는다.
  const storyReached = useStoryReached();
  const inView = useInView(ref);

  return (
    // 섹션 하단은 영상이 덮을 뿐 배경이 없어, 소수점 배율에서 다음 섹션과의 경계에
    // 흰 바탕이 한 줄 비친다. 마지막 두 픽셀을 어두운 띠로 덮어 가린다.
    <section
      ref={ref}
      data-main-transition
      className="relative after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-[#0D0E10] after:content-['']"
    >
      <ScrollStage
        heightVh={200}
        startHoldPx={400}
        mobileUnderHeader
        className="[clip-path:inset(0)]"
      >
        {progress => (
          <TransitionStage
            active={storyReached || inView}
            progress={progress}
            question={t.rich('question', customRichTextTags)}
            answer={t.rich('answer', customRichTextTags)}
          />
        )}
      </ScrollStage>
    </section>
  );
}
